import { Component, computed, effect, inject, input, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { EMPTY, firstValueFrom, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap } from 'rxjs/operators';
import { api } from '@consts/url.const';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { IntelligenceModels } from '@models/intelligence.model';
import { HomeModel } from '@models/home.model';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { IntelligenceResultsService } from '../../../../../services/intelligence-results.service';
import {
  PreloaderColor,
  UiButtonComponent,
  UiDeviceListComponent,
  UiFilterChipComponent,
  UiPreloaderComponent,
} from '@ui';
import { MatTooltip } from '@angular/material/tooltip';
// The web-local search box, as used by the device-list page's own filter bar and
// by every sibling Cmd+K panel — NOT the `@ui` one, which looks different.
import { UiSearchComponent } from '../../../../ui-kit/ui-search/ui-search.component';
import { UiCameraStatusSnapshotComponent } from '../../../../ui-kit/ui-camera-status-snapshot/ui-camera-status-snapshot.component';
import { UiUserSettingsDateComponent } from '../../../../ui-kit/ui-user-settings-date/ui-user-settings-date.component';
import { UiUserSettingsDateType } from '@enums/shared.enum';
import {
  TestSelectedCameraModalComponent,
  TestSelectedCameraModalData,
} from 'src/app/pages/location-v2/pages/device/pages/device-list/components/test-selected-camera-modal/test-selected-camera-modal.component';
import { BulkUpdateFormComponent } from 'src/app/pages/location-v2/pages/device/pages/device-list/components/bulk-update-form-modal/bulk-update-form.component';
import { ChangePasswordDialogComponent } from 'src/app/pages/location-v2/pages/device/pages/device-list/components/change-password-dialog/change-password-dialog.component';
import { PulsationModels } from '@models/pulsation.model';
import {
  linkSpeedLabel,
  pingStatus,
  toCameraChips,
  toCameraMetrics,
  toCoreChips,
  toCoreMetrics,
} from './device-health-result.util';
import {
  ADVANCED_OPTIONS,
  facetLabel,
  filtersCacheKey,
  resolveSeededLocationFilters,
  STATUS_OPTIONS,
  toFilters,
  toHttpParams,
  TYPE_OPTIONS,
} from './device-health-filters.util';

import AdvancedFilter = IntelligenceModels.DeviceHealth.AdvancedFilter;
import DeviceHealthFilters = IntelligenceModels.DeviceHealth.Filters;
import FacetOption = IntelligenceModels.DeviceHealth.FacetOption;
import FilterChip = IntelligenceModels.DeviceHealth.FilterChip;
import FilterKind = IntelligenceModels.DeviceHealth.FilterKind;
import FilterLoad = IntelligenceModels.DeviceHealth.FilterLoad;
import FiltersForm = IntelligenceModels.DeviceHealth.FiltersForm;

const ROW_HEIGHT_PX = 52;
const SELECTION_BAR_PADDING_PX = 96;
/** Vertical padding of the expanded detail panel, plus its section gaps. */
const DETAILS_PADDING_PX = 32;
/** One row of component chips. */
const CHIP_ROW_HEIGHT_PX = 30;
/** One row of metric columns (label above value). */
const METRIC_ROW_HEIGHT_PX = 38;
/** A network interface card, and the one-line form a cable-less one collapses to. */
const NETWORK_CARD_HEIGHT_PX = 112;
/**
 * Columns the chip and metric grids fit at the panel's usual width. Only the
 * first-paint estimate depends on this — `ui-device-list` measures the real
 * height right after, so a narrower panel that wraps to fewer columns corrects
 * itself instead of clipping.
 */
const CHIPS_PER_ROW = 3;
const METRICS_PER_ROW = 3;
/** Placeholder while details are still loading (or failed). */
const DETAILS_PLACEHOLDER_HEIGHT_PX = 48;
/**
 * Backstop of the commit gate. Every request rebuilds the whole org snapshot
 * server-side, and `mat-select multiple` emits per option toggle, so the selects
 * commit on overlay close and this only collapses whatever still arrives in a
 * burst. `ui-search` debounces itself, so the search rides on top of its own.
 */
const COMMIT_DEBOUNCE_MS = 300;

function buildFiltersForm(): FiltersForm {
  return new FormGroup({
    name: new FormControl<string>('', { nonNullable: true }),
    types: new FormControl<IntelligenceModels.DeviceList.DeviceType[]>([], { nonNullable: true }),
    statuses: new FormControl<IntelligenceModels.HealthStatus[]>([], { nonNullable: true }),
    locations: new FormControl<string[]>([], { nonNullable: true }),
    tagIds: new FormControl<string[]>([], { nonNullable: true }),
    advanced: new FormControl<AdvancedFilter[]>([], { nonNullable: true }),
  });
}

function readFilters(form: FiltersForm): DeviceHealthFilters {
  return toFilters(form.getRawValue());
}

/**
 * Flat cameras + Cores health list (no location grouping) for the Cmd+K panel.
 * The overall status is computed server-side by the canonical device-health
 * rule, so this component only renders what it receives.
 */
@Component({
  selector: 'app-device-health-result',
  standalone: true,
  imports: [
    CommonModule,
    MatCheckbox,
    MatSelectModule,
    MatTooltip,
    ReactiveFormsModule,
    TranslatePipe,
    UiButtonComponent,
    UiDeviceListComponent,
    UiFilterChipComponent,
    UiPreloaderComponent,
    UiSearchComponent,
    UiCameraStatusSnapshotComponent,
    UiUserSettingsDateComponent,
  ],
  templateUrl: './device-health-result.component.html',
  styleUrls: ['./device-health-result.component.scss'],
})
export class DeviceHealthResultComponent {
  private readonly intelligenceResultsService = inject(IntelligenceResultsService);
  private readonly matDialog = inject(MatDialog);
  private readonly store: Store = inject(Store);
  private readonly httpClient = inject(HttpClient);

  /**
   * Optional direct input; when null the component reads the current result
   * from the results service.
   */
  public readonly response = input<IntelligenceModels.DeviceHealth.Response | null>(null);

  public readonly selectedCameraIds = signal<ReadonlySet<string>>(new Set());
  public readonly selectedCount = computed<number>(() => this.selectedCameraIds().size);
  public readonly enableChangePasswordFeature = toSignal(
    this.store.select(OrganizationSelectors.selectEnableChangePasswordFeature),
    { initialValue: false },
  );

  public readonly rowHeight = ROW_HEIGHT_PX;
  protected readonly PreloaderColor: typeof PreloaderColor = PreloaderColor;
  protected readonly deviceType: typeof IntelligenceModels.DeviceList.DeviceType =
    IntelligenceModels.DeviceList.DeviceType;
  /** Same MM/DD/YYYY format the device-list page uses for the oldest-footage date. */
  protected readonly dateTypeFormat: UiUserSettingsDateType = UiUserSettingsDateType.mdy;

  /**
   * The tool result that SEEDS the filter form. Input-first, because the live
   * render path binds `[response]`; keying the seeding effect on
   * `intelligenceResultsService.current()` instead would watch a source the
   * template does not use.
   *
   * The effect below re-seeds on this REFERENCE changing, which today means "a
   * new tool call" — both paths hand over the stored `data` object verbatim. A
   * future `patchActiveData` path for device-health, or a host that rebuilds the
   * input object per tick, would read as a new tool call and wipe the user's
   * picks (plan §8).
   */
  private readonly seedResponse = computed<IntelligenceModels.DeviceHealth.Response | null>(() => {
    const direct = this.response();
    if (direct) return direct;
    const result = this.intelligenceResultsService.current();
    return result?.type === 'device-health'
      ? (result.data as IntelligenceModels.DeviceHealth.Response)
      : null;
  });

  /** The filter bar. Owns the filter state after the seed (plan §3.2). */
  public readonly filtersForm: FiltersForm = buildFiltersForm();

  /**
   * The form's value as a normalized filter set — the reactive mirror the chips
   * and the availability rules read. Updated on every keystroke and option
   * toggle, so the bar stays instant while the fetch waits for the commit gate.
   */
  public readonly formFilters: WritableSignal<DeviceHealthFilters> = signal(toFilters());

  /** Response of the last committed refilter; null while the seed is on screen. */
  private readonly localResponse = signal<IntelligenceModels.DeviceHealth.Response | null>(null);
  public readonly refetching: WritableSignal<boolean> = signal(false);
  public readonly refetchFailed: WritableSignal<boolean> = signal(false);

  /** List responses by canonical filter key; cleared whenever a new seed lands. */
  private readonly responseCache = new Map<string, IntelligenceModels.DeviceHealth.Response>();
  /** Canonical key of the filter set that produced the rendered response. */
  private renderedKey: string = filtersCacheKey(toFilters());
  /** One settled filter intent, per the commit gate (plan §5.7). */
  private readonly filtersCommitted = new Subject<void>();
  /**
   * Bumped by every seed. A request that started before the current seed is
   * dropped when it lands: the tool result's own filters won, and letting the
   * stale list through would silently replace them.
   */
  private seedGeneration: number = 0;

  /** The response on screen: the refilter's when there is one, else the seed's. */
  private readonly viewResponse = computed<IntelligenceModels.DeviceHealth.Response | null>(
    () => this.localResponse() ?? this.seedResponse(),
  );

  /**
   * Dropdown sources come from the response being rendered, and the backend
   * builds them BEFORE filtering, so picking a location never collapses the
   * dropdown.
   */
  public readonly locationOptions = computed<FacetOption[]>(
    () => this.viewResponse()?.facets.locations ?? [],
  );
  public readonly tagOptions = computed<FacetOption[]>(() => this.viewResponse()?.facets.tags ?? []);

  /**
   * A facet with nothing to choose from hides its control rather than opening an
   * empty dropdown — an organization with no tags gets no Tags select. A value
   * already applied keeps its control visible so it can still be cleared there.
   */
  public readonly showLocationFilter = computed<boolean>(
    () => this.locationOptions().length > 0 || this.formFilters().locations.length > 0,
  );
  public readonly showTagFilter = computed<boolean>(
    () => this.tagOptions().length > 0 || this.formFilters().tagIds.length > 0,
  );

  public readonly hasFilterBar = computed<boolean>(() => !!this.viewResponse());

  protected readonly typeOptions: IntelligenceModels.DeviceHealth.FilterOption<
    IntelligenceModels.DeviceList.DeviceType
  >[] = TYPE_OPTIONS;
  protected readonly statusOptions: IntelligenceModels.DeviceHealth.FilterOption<
    IntelligenceModels.HealthStatus
  >[] = STATUS_OPTIONS;
  protected readonly advancedOptions: IntelligenceModels.DeviceHealth.FilterOption<AdvancedFilter>[] =
    ADVANCED_OPTIONS;

  public constructor() {
    this.filtersForm.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.formFilters.set(readFilters(this.filtersForm)));

    // `ui-search` debounces its own output, so a settled search IS an intent.
    this.filtersForm.controls.name.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.filtersCommitted.next());

    this.filtersCommitted
      .pipe(
        debounceTime(COMMIT_DEBOUNCE_MS),
        map(() => this.formFilters()),
        filter((filters) => filtersCacheKey(filters) !== this.renderedKey),
        switchMap((filters) => this.loadFilters(filters)),
        takeUntilDestroyed(),
      )
      .subscribe((load) => this.applyResponse(load));

    effect(() => this.seedFilters(this.seedResponse()));
  }

  /**
   * Re-seeds the form from a tool result, deliberately overriding whatever the
   * user had picked by hand (plan §3.2). `detailsById` survives — per-device data
   * is filter-independent — while the camera selection resets with the new
   * context.
   */
  private seedFilters(response: IntelligenceModels.DeviceHealth.Response | null): void {
    const filters = resolveSeededLocationFilters(toFilters(response?.filters), response?.facets?.locations ?? []);

    this.seedGeneration += 1;
    this.responseCache.clear();
    this.localResponse.set(null);
    this.refetching.set(false);
    this.refetchFailed.set(false);
    this.renderedKey = filtersCacheKey(filters);
    if (response) this.responseCache.set(this.renderedKey, response);

    this.formFilters.set(filters);
    this.filtersForm.setValue({ ...filters, name: filters.name ?? '' }, { emitEvent: false });

    this.selectedCameraIds.set(new Set());
    this.expandedId.set(null);
  }

  /**
   * One committed filter set → the list that renders it. A combination already
   * seen renders straight from the cache; `switchMap` upstream makes the newest
   * request the winning one for free.
   */
  private loadFilters(filters: DeviceHealthFilters): Observable<FilterLoad> {
    const key = filtersCacheKey(filters);
    const cached = this.responseCache.get(key);
    if (cached) {
      this.refetching.set(false);
      this.refetchFailed.set(false);
      return of({ key, response: cached });
    }

    const generation = this.seedGeneration;
    this.refetching.set(true);
    this.refetchFailed.set(false);
    return this.httpClient
      .get<IntelligenceModels.DeviceHealth.Response>(api.intelligence.deviceHealth, {
        params: toHttpParams(filters),
      })
      .pipe(
        filter(() => generation === this.seedGeneration),
        map((response) => ({ key, response })),
        // A failed refilter keeps the working list on screen and reports itself
        // on one line — blanking the list over one bad request is worse.
        catchError(() => {
          if (generation === this.seedGeneration) {
            this.refetching.set(false);
            this.refetchFailed.set(true);
          }
          return EMPTY;
        }),
      );
  }

  private applyResponse(load: FilterLoad): void {
    this.responseCache.set(load.key, load.response);
    this.renderedKey = load.key;
    this.localResponse.set(load.response);
    this.refetching.set(false);
    this.pruneToItems(load.response.items);
  }

  /**
   * Drops state that points at devices the new result does not contain:
   * otherwise checked-but-invisible cameras ride into the bulk actions, and the
   * expanded row id dangles.
   */
  private pruneToItems(items: IntelligenceModels.DeviceHealth.Item[]): void {
    const ids = new Set(items.map((item) => item.id));

    this.selectedCameraIds.update((current) => {
      const next = new Set([...current].filter((id) => ids.has(id)));
      return next.size === current.size ? current : next;
    });

    const expanded = this.expandedId();
    if (expanded && !ids.has(expanded)) this.expandedId.set(null);
  }

  /**
   * Commits one filter intent. Called on a multi-select's overlay close, not on
   * every option toggle, so six ticks are one request rather than six org-wide
   * aggregations (plan §5.7).
   */
  public commitFilters(): void {
    this.filtersCommitted.next();
  }

  /**
   * The search box is `model()`-based rather than a `ControlValueAccessor`, so
   * its already-debounced value is written into the form control by hand — the
   * control stays the one source the chips, the cache key and the seed read.
   */
  public onSearchChanged(value: string | null): void {
    this.filtersForm.controls.name.setValue(value ?? '');
  }

  public removeFilterChip(chip: FilterChip): void {
    const controls = this.filtersForm.controls;

    if (chip.kind === FilterKind.Name) {
      controls.name.setValue('');
    } else if (chip.kind === FilterKind.Types) {
      controls.types.setValue(controls.types.value.filter((value) => value !== chip.value));
    } else if (chip.kind === FilterKind.Statuses) {
      controls.statuses.setValue(controls.statuses.value.filter((value) => value !== chip.value));
    } else if (chip.kind === FilterKind.Locations) {
      controls.locations.setValue(controls.locations.value.filter((value) => value !== chip.value));
    } else if (chip.kind === FilterKind.TagIds) {
      controls.tagIds.setValue(controls.tagIds.value.filter((value) => value !== chip.value));
    } else {
      controls.advanced.setValue(controls.advanced.value.filter((value) => value !== chip.value));
    }

    this.filtersCommitted.next();
  }

  public clearAllFilters(): void {
    this.filtersForm.setValue({ name: '', types: [], statuses: [], locations: [], tagIds: [], advanced: [] });
    this.filtersCommitted.next();
  }

  /** Id of the single expanded accordion row, or null when all are collapsed. */
  private readonly expandedId = signal<string | null>(null);
  /** Details fetched per device on expand; the list response never carries them. */
  private readonly detailsById = signal<ReadonlyMap<string, IntelligenceModels.DeviceHealth.Details>>(
    new Map(),
  );
  private readonly loadingDetailsId = signal<string | null>(null);
  private readonly refreshingDetailsId = signal<string | null>(null);
  private readonly failedDetailsId = signal<string | null>(null);

  /**
   * Rows handed to the virtualizer.
   *
   * Deliberately returns a NEW array whenever expansion or detail state
   * changes: `ui-device-list` re-measures its rendered rows in an effect keyed
   * on this input, which is what keeps the virtualizer's offsets correct after
   * a row grows. `rowKey` also folds the expanded flag in, so an expanded row
   * misses the measurement cache and falls back to `estimateRowSize` first.
   */
  public readonly items = computed<IntelligenceModels.DeviceHealth.Item[]>(() => {
    this.expandedId();
    this.detailsById();
    this.loadingDetailsId();
    this.refreshingDetailsId();
    this.failedDetailsId();
    return [...(this.viewResponse()?.items ?? [])];
  });

  public readonly counts = computed<IntelligenceModels.DeviceHealth.Counts>(
    () => this.viewResponse()?.counts ?? { online: 0, unhealthy: 0, offline: 0, installing: 0 },
  );

  /**
   * Applied filters, rendered as removable chips below the bar. One chip per
   * value: the filters are multi-value, and joining an array into one chip would
   * print `location "a,b"`.
   *
   * Read off the FORM, not off the response, so a chip disappears the moment its
   * value is unpicked instead of one refetch later. A location or tag id with no
   * matching facet — a prompt-seeded partial location NAME, say — keeps its raw
   * value as its wording, which is also what gets sent back on refetch.
   */
  public readonly filterChips = computed<FilterChip[]>(() => {
    const filters = this.formFilters();
    const locations = this.locationOptions();
    const tags = this.tagOptions();

    const chips: FilterChip[] = [
      ...filters.types.map((value) => ({
        kind: FilterKind.Types,
        labelKey: 'DEVICE_HEALTH.TYPE',
        value,
        valueLabelKey: TYPE_OPTIONS.find((option) => option.value === value)?.labelKey,
        valueLabel: value,
      })),
      ...filters.statuses.map((value) => ({
        kind: FilterKind.Statuses,
        labelKey: 'DEVICE_HEALTH.STATUS',
        value,
        valueLabelKey: STATUS_OPTIONS.find((option) => option.value === value)?.labelKey,
        valueLabel: value,
      })),
      ...filters.locations.map((value) => ({
        kind: FilterKind.Locations,
        labelKey: 'DEVICE_HEALTH.LOCATION',
        value,
        valueLabel: facetLabel(locations, value),
      })),
      ...filters.tagIds.map((value) => ({
        kind: FilterKind.TagIds,
        labelKey: 'DEVICE_LIST.PLACEHOLDERS.TAGS_FILTERS',
        value,
        valueLabel: facetLabel(tags, value),
      })),
      ...filters.advanced.map((value) => ({
        kind: FilterKind.Advanced,
        labelKey: 'DEVICE_LIST.PLACEHOLDERS.ADVANCED_FILTERS',
        value,
        valueLabelKey: IntelligenceModels.DeviceHealth.AdvancedFilterLabelKey[value],
        valueLabel: value,
      })),
    ];

    if (filters.name) {
      chips.push({
        kind: FilterKind.Name,
        labelKey: 'DEVICE_HEALTH.NAME',
        value: filters.name,
        valueLabel: filters.name,
      });
    }

    return chips;
  });

  public readonly selectedCameras = computed<HomeModel.Camera[]>(() =>
    this.items()
      .filter(
        (item) =>
          item.type === IntelligenceModels.DeviceList.DeviceType.Camera &&
          this.selectedCameraIds().has(item.id),
      )
      .map((item) => ({
        cameraId: item.id,
        edgeId: item.edgeId ?? '',
        locationId: item.locationId,
        name: item.name,
      })),
  );

  public readonly rowKey = (item: IntelligenceModels.DeviceHealth.Item): string =>
    `${item.type}:${item.id}:${this.expandedId() === item.id ? 'open' : 'closed'}`;

  public readonly estimateRowSize = (item: IntelligenceModels.DeviceHealth.Item): number =>
    this.expandedId() === item.id ? ROW_HEIGHT_PX + this.estimateDetailsHeight(item) : ROW_HEIGHT_PX;

  public isCamera(item: IntelligenceModels.DeviceHealth.Item): boolean {
    return item.type === IntelligenceModels.DeviceList.DeviceType.Camera;
  }

  public isExpanded(item: IntelligenceModels.DeviceHealth.Item): boolean {
    return this.expandedId() === item.id;
  }

  public isLoadingDetails(item: IntelligenceModels.DeviceHealth.Item): boolean {
    return this.loadingDetailsId() === item.id;
  }

  public hasFailedDetails(item: IntelligenceModels.DeviceHealth.Item): boolean {
    return this.failedDetailsId() === item.id;
  }

  public isRefreshingDetails(item: IntelligenceModels.DeviceHealth.Item): boolean {
    return this.refreshingDetailsId() === item.id;
  }

  public details(
    item: IntelligenceModels.DeviceHealth.Item,
  ): IntelligenceModels.DeviceHealth.Details | undefined {
    return this.detailsById().get(item.id);
  }

  /** Ping stats of an expanded camera row — the dialog's "Ping (ms)" row. */
  public cameraPing(
    item: IntelligenceModels.DeviceHealth.Item,
  ): PulsationModels.PingStats | undefined {
    const details = this.details(item);
    return details && details.type === IntelligenceModels.DeviceList.DeviceType.Camera
      ? details.ping
      : undefined;
  }

  /**
   * The ping row's status glyph, through the dialog's own `pingStateToStatus`
   * mapping and then the same icon set `ui-v2-component-status` draws: Online →
   * green, Offline → red, everything else the warning glyph (its default case).
   */
  public pingIconClass(ping: PulsationModels.PingStats): string {
    const status = pingStatus(ping.state);
    if (status === PulsationModels.ComponentStatus.Online) return 'ui-icon-status-online';
    if (status === PulsationModels.ComponentStatus.Offline) return 'ui-icon-status-offline';
    return 'ui-icon-status-unhealthy';
  }

  public coreNetwork(
    item: IntelligenceModels.DeviceHealth.Item,
  ): IntelligenceModels.DeviceHealth.NetworkInterface[] {
    const details = this.details(item);
    return details && details.type === IntelligenceModels.DeviceList.DeviceType.Core
      ? details.network
      : [];
  }

  /**
   * Opens one accordion row at a time, fetching its breakdown from the
   * per-device route on first expand and caching it for the rest of the
   * session. The list request stays detail-free on every path.
   */
  public async toggleRow(item: IntelligenceModels.DeviceHealth.Item): Promise<void> {
    if (this.expandedId() === item.id) {
      this.expandedId.set(null);
      return;
    }

    this.expandedId.set(item.id);
    this.failedDetailsId.set(null);

    if (this.detailsById().has(item.id) || this.loadingDetailsId() === item.id) return;

    this.loadingDetailsId.set(item.id);
    try {
      const response = await firstValueFrom(
        this.httpClient.get<IntelligenceModels.DeviceHealth.DetailResponse>(
          api.intelligence.deviceHealthDetails(item.id),
        ),
      );
      const details = response?.item?.details;
      if (details) {
        this.detailsById.update((current) => new Map(current).set(item.id, details));
      } else {
        this.failedDetailsId.set(item.id);
      }
    } catch {
      this.failedDetailsId.set(item.id);
    } finally {
      this.loadingDetailsId.update((current) => (current === item.id ? null : current));
    }
  }

  /**
   * Re-reads one device without disturbing the active filters or the other
   * expanded-row caches. The detail response also includes the current list
   * item, so its status dot and issue count stay in sync with the breakdown.
   */
  public async refreshRow(item: IntelligenceModels.DeviceHealth.Item): Promise<void> {
    if (this.refreshingDetailsId()) return;

    this.refreshingDetailsId.set(item.id);
    this.failedDetailsId.set(null);

    try {
      const response = await firstValueFrom(
        this.httpClient.get<IntelligenceModels.DeviceHealth.DetailResponse>(
          api.intelligence.deviceHealthDetails(item.id),
        ),
      );
      const refreshedItem = response?.item;
      if (!refreshedItem?.details) {
        this.detailsById.update((current) => {
          const next = new Map(current);
          next.delete(item.id);
          return next;
        });
        this.failedDetailsId.set(item.id);
        return;
      }

      this.detailsById.update((current) => new Map(current).set(item.id, refreshedItem.details));
      this.patchItem(refreshedItem);
    } catch {
      this.failedDetailsId.set(item.id);
    } finally {
      this.refreshingDetailsId.update((current) => (current === item.id ? null : current));
    }
  }

  private patchItem(item: IntelligenceModels.DeviceHealth.Item): void {
    const patch = (
      response: IntelligenceModels.DeviceHealth.Response,
    ): IntelligenceModels.DeviceHealth.Response => ({
      ...response,
      items: response.items.map((current) => (current.id === item.id ? { ...item, details: undefined } : current)),
    });

    for (const [key, response] of this.responseCache) this.responseCache.set(key, patch(response));

    const current = this.viewResponse();
    if (current) this.localResponse.set(patch(current));
  }

  /** Component states of an expanded row, as one scannable strip of chips. */
  public healthChips(
    item: IntelligenceModels.DeviceHealth.Item,
  ): IntelligenceModels.DeviceHealth.HealthChip[] {
    const details = this.details(item);
    if (!details) return [];

    return details.type === IntelligenceModels.DeviceList.DeviceType.Camera
      ? toCameraChips(details)
      : toCoreChips(details);
  }

  /** Metadata fields of an expanded row, rendered as label-above-value columns. */
  public metrics(
    item: IntelligenceModels.DeviceHealth.Item,
  ): IntelligenceModels.DeviceHealth.HealthMetric[] {
    const details = this.details(item);
    if (!details) return [];

    return details.type === IntelligenceModels.DeviceList.DeviceType.Camera
      ? toCameraMetrics(details)
      : toCoreMetrics(details);
  }

  public chipToneClass(tone: IntelligenceModels.DeviceHealth.HealthChipTone): string {
    return `health-chip-${tone}`;
  }

  /**
   * A chip's glyph: its own when it has one (trained), otherwise the status icon
   * of its tone — the same three icons the summary counters use.
   */
  public chipIconClass(chip: IntelligenceModels.DeviceHealth.HealthChip): string {
    if (chip.iconClass) return chip.iconClass;
    if (chip.tone === IntelligenceModels.DeviceHealth.HealthChipTone.Ok) {
      return 'ui-icon-status-online';
    }
    if (chip.tone === IntelligenceModels.DeviceHealth.HealthChipTone.Error) {
      return 'ui-icon-status-offline';
    }
    if (chip.tone === IntelligenceModels.DeviceHealth.HealthChipTone.Warning) {
      return 'ui-icon-status-unhealthy';
    }
    return 'ui-icon-cross';
  }

  public linkSpeed(netInterface: IntelligenceModels.DeviceHealth.NetworkInterface): string {
    return linkSpeedLabel(netInterface.linkSpeedMbps);
  }

  /** Matches the device-list table, including its `-1 Mbps` sentinel value. */
  public rawLinkSpeed(netInterface: IntelligenceModels.DeviceHealth.NetworkInterface): string {
    return netInterface.linkSpeedMbps === undefined ? '—' : `${netInterface.linkSpeedMbps} Mbps`;
  }

  /** Gates the speed badge — an interface can be up and report no speed. */
  public hasLinkSpeed(netInterface: IntelligenceModels.DeviceHealth.NetworkInterface): boolean {
    return !!netInterface.linkSpeedMbps && netInterface.linkSpeedMbps > 0;
  }

  /**
   * Wording of the collapsed row's issue badge. Server-provided, so a device
   * whose composite status is Online but whose substream is dead is visible
   * WITHOUT expanding it.
   */
  public issueLabelKey(item: IntelligenceModels.DeviceHealth.Item): string {
    return item.issueCount === 1 ? 'DEVICE_HEALTH.ISSUE' : 'DEVICE_HEALTH.ISSUES';
  }

  public issueLabelParams(
    item: IntelligenceModels.DeviceHealth.Item,
  ): IntelligenceModels.DeviceHealth.DetailParams {
    return { count: item.issueCount ?? 0 };
  }

  /**
   * First-paint height guess for an expanded row. `ui-device-list` measures the
   * real DOM afterwards, so this only has to be close enough to avoid a jump.
   */
  private estimateDetailsHeight(item: IntelligenceModels.DeviceHealth.Item): number {
    if (!this.details(item)) return DETAILS_PLACEHOLDER_HEIGHT_PX;

    const chips = Math.ceil(this.healthChips(item).length / CHIPS_PER_ROW) * CHIP_ROW_HEIGHT_PX;
    const metrics = Math.ceil(this.metrics(item).length / METRICS_PER_ROW) * METRIC_ROW_HEIGHT_PX;
    const network = this.coreNetwork(item).length * NETWORK_CARD_HEIGHT_PX;

    return DETAILS_PADDING_PX + chips + metrics + network;
  }

  public isCameraSelected(cameraId: string): boolean {
    return this.selectedCameraIds().has(cameraId);
  }

  public selectCamera(cameraId: string, change: MatCheckboxChange): void {
    this.selectedCameraIds.update((current) => {
      const next = new Set(current);
      if (change.checked) next.add(cameraId);
      else next.delete(cameraId);
      return next;
    });
  }

  public selectAllCameras(): void {
    this.selectedCameraIds.set(
      new Set(this.items().filter((item) => this.isCamera(item)).map((item) => item.id)),
    );
  }

  public clearSelectedCameras(): void {
    this.selectedCameraIds.set(new Set());
  }

  public testSelected(): void {
    const cameras = this.selectedCameras();
    const data: TestSelectedCameraModalData = {
      cameraIds: cameras.map((camera) => camera.cameraId),
      edgeIds: [...new Set(cameras.map((camera) => camera.edgeId))],
    };

    this.matDialog
      .open(TestSelectedCameraModalComponent, {
        panelClass: 'modal-no-padding',
        height: '80vh',
        width: '85vw',
        data,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) this.clearSelectedCameras();
      });
  }

  public manageSelected(): void {
    this.matDialog
      .open(BulkUpdateFormComponent, {
        panelClass: 'modal-no-padding',
        data: { camerasToManage: this.selectedCameras() },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) this.clearSelectedCameras();
      });
  }

  public changePasswordSelected(): void {
    this.matDialog
      .open(ChangePasswordDialogComponent, {
        panelClass: 'modal-no-padding',
        data: { cameras: this.selectedCameras() },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) this.clearSelectedCameras();
      });
  }

  public paddingEnd(): number {
    return this.selectedCount() ? SELECTION_BAR_PADDING_PX : 0;
  }

  public statusIconClass(status: IntelligenceModels.HealthStatus): string {
    if (status === IntelligenceModels.HealthStatus.Online) return 'ui-icon-status-online';
    if (status === IntelligenceModels.HealthStatus.Unhealthy) return 'ui-icon-status-unhealthy';
    // Web parity: `ui-device-status` draws Installing with the unhealthy glyph
    // (its tooltip is what disambiguates them — see statusLabelKey).
    if (status === IntelligenceModels.HealthStatus.Installing) return 'ui-icon-status-unhealthy';
    return 'ui-icon-status-offline';
  }

  public typeLabelKey(item: IntelligenceModels.DeviceHealth.Item): string {
    return this.isCamera(item) ? 'DEVICE_HEALTH.CAMERA' : 'DEVICE_HEALTH.CORE';
  }

  public statusLabelKey(status: IntelligenceModels.HealthStatus): string {
    if (status === IntelligenceModels.HealthStatus.Online) return 'DEVICE_HEALTH.ONLINE';
    if (status === IntelligenceModels.HealthStatus.Unhealthy) return 'DEVICE_HEALTH.UNHEALTHY';
    if (status === IntelligenceModels.HealthStatus.Installing) return 'DEVICE_STATUS.INSTALLING';
    return 'DEVICE_HEALTH.OFFLINE';
  }
}
